/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import Switch from 'carbon-react/lib/components/switch';

import { VIEW_TYPES } from '../../../../core/types';
import styles from './cash-flow-switch.scss';
import cashFlowView from '../../../onboarding/arrows/cash-flow-view.png';
import {
  SCENARIO_ONBOARDING_STEP,
  SCENARIO_WITH_INVOICES_ONBOARDING_STEP
} from '../../../../forecast/onboarding/const';
import { useAnimationLatency, useOnboardingUrlInfo } from '../../../../forecast/onboarding/utils/hooks';
import { ForecastType } from '../../../../forecast/onboarding/shared/onboarding-type-select';
import { PREDICTIONS_SUB_PAGES_IDS } from '../navigation/const';
import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { trackEvent } from '../../../../core/tracker';
import { TRACKING_EVENTS } from '../../../../core/constants';
import { useDataGridScrollContext } from '../data-grid-scroll-context';

export const CashFlowSwitch = () => {
  const { setViewType, viewType } = usePredictionsInfoContext();

  const { updateScrollPosition } = useDataGridScrollContext();

  const { pathname } = useLocation();

  const {
    forecastType,
    onboardingStep,
    isOnboardingScenarioRoute,
    isOnboardingScenarioWithInvoicesRoute
  } = useOnboardingUrlInfo();

  const handleViewTypeChange = () => {
    updateScrollPosition(viewType !== VIEW_TYPES.CASH_FLOW);
    trackEvent(TRACKING_EVENTS.TOGGLED_ON_CASH_FLOW_VIEW);
    if (viewType !== VIEW_TYPES.CASH_FLOW) {
      setViewType?.(VIEW_TYPES.CASH_FLOW);
    } else {
      const urlBits = pathname.split('/');
      const page = `${urlBits[urlBits.length - 2]}-${urlBits[urlBits.length - 1]}`;
      if (
        page === PREDICTIONS_SUB_PAGES_IDS.PREDICTIONS_ASSETS ||
        page === PREDICTIONS_SUB_PAGES_IDS.PREDICTIONS_LIABILITIES
      ) {
        setViewType?.(VIEW_TYPES.ACCRUALS_BS);
      } else {
        setViewType?.(VIEW_TYPES.ACCRUALS_PL);
      }
    }
  };

  const showOnboardingArrow =
    (isOnboardingScenarioRoute &&
      onboardingStep === SCENARIO_ONBOARDING_STEP.SCRATCH_CONTROL_CASH_FLOW &&
      forecastType === ForecastType.FromScratch) ||
    (isOnboardingScenarioRoute &&
      onboardingStep === SCENARIO_ONBOARDING_STEP.AUTO_PREDICTIONS_CONTROL_CASH_FLOW &&
      forecastType === ForecastType.WithAutoPredictions) ||
    (isOnboardingScenarioRoute &&
      onboardingStep === SCENARIO_ONBOARDING_STEP.LYA_CONTROL_CASH_FLOW &&
      forecastType === ForecastType.FromLYA) ||
    (isOnboardingScenarioWithInvoicesRoute &&
      onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.SCRATCH_CONTROL_CASH_FLOW &&
      forecastType === ForecastType.FromScratch) ||
    (isOnboardingScenarioWithInvoicesRoute &&
      onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.AUTO_PREDICTIONS_CONTROL_CASH_FLOW &&
      forecastType === ForecastType.WithAutoPredictions) ||
    (isOnboardingScenarioWithInvoicesRoute &&
      onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.LYA_CONTROL_CASH_FLOW &&
      forecastType === ForecastType.FromLYA);

  const showAnimatedArrow = useAnimationLatency(showOnboardingArrow);

  return (
    <>
      {showOnboardingArrow && (
        <img
          alt="onboarding-arrow"
          src={cashFlowView}
          style={{ position: 'absolute', top: 4, left: 28 }}
          className={classNames(styles.fadeInContainer, {
            [styles.fadeIn]: showAnimatedArrow
          })}
        />
      )}
      <Switch
        id="cash-flow-view-switch"
        checked={viewType === VIEW_TYPES.CASH_FLOW}
        onChange={handleViewTypeChange}
        size="small"
        mr="12px"
      />
      <label htmlFor="cash-flow-view-switch" className={styles.switchLabel}>
        <FormattedMessage id="predict.cash_flow_view" defaultMessage="Cash flow view" />
      </label>
    </>
  );
};
